import { useEffect, useState } from "react";
import CustomButton from "../../../components/Form/ArrowButton";
import CustomDropDown from "../../../components/Form/Dropdown";
import { useNavigate } from "react-router-dom";
import ProgressBar from "../../../components/Form/ProgressBar";
import { useSelector, useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import useForm from "../../../hooks/useForm";
import { USER_INPUT_FIELD_TYPES } from "../../../constants/userInputFieldNames";
import LoginInput from "../../../components/Form/LoginInput";
import { placeOrder, setFormDataFiles, setFormDataGlobState } from "../../../store";
import { useThunk } from "../../../hooks/useThunk";
import { REERENCE_STYLES, SUBJECTS_ARRAY } from "../../../Data/orderProperties";
import UploadIcon from "../../../assets/Icons/upload.svg";
import showToast from "../../../utils/toastNotifications";
import useFirebaseFileUpload from "../../../hooks/useFirebaseFileUpload";
import { referencesFormats } from "../../../constants/commonConstants";
import { ClipLoader } from "react-spinners";
import CustomFileIcon from "../../../components/CustomFileIcon";

const PaperInstructions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const createOrder = useSelector((state) => state.createOrder);

  // Thunks
  const [doPlaceOrder, isPlacingOrder, placeOrderError] = useThunk(placeOrder);

  const [
    doUploadRefrencesToFirebase,
    isUploadRefrencesToFirebaseLoading,
    uploadRefrencesToFirebaseError,
  ] = useFirebaseFileUpload();

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      ...createOrder.formData,
      subject: "",
      topic: "Any Topic",
      orderInstructions: "",
      referenceStyle: "APA",
    },
    {
      subject: true,
      topic: false,
      orderInstructions: true,
      referenceStyle: true,
      writingType: true,
      typeOfPaper: true,
      academicLevel: true,
      typeOfEnglish: true,
      deadline: true,
      spacing: true,
      pages: false,
      slides: false,
      needDocumentation: false,
      needPresentation: false,
      budgetIncrement: true
    },
    {
      subject: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      topic: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      orderInstructions: USER_INPUT_FIELD_TYPES.MIN_30_CHARACTORS,
      referenceStyle: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      writingType: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfPaper: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      academicLevel: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfEnglish: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      deadline: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      spacing: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      pages: USER_INPUT_FIELD_TYPES.NUMBER_FIELD,
      slides: USER_INPUT_FIELD_TYPES.NUMBER_FIELD,
      needDocumentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      needPresentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      budgetIncrement: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  // Progressbar functions
  const steps = [
    { fullLabel: "Calculation", shortLabel: "Calculation" },
    { fullLabel: "Paper Instructions", shortLabel: "Instructions" },
    { fullLabel: "Secure Payment", shortLabel: "Payment" },
  ];

  // State to store user reference files
  const [uploadFiles, setUploadFiles] = useState([]);

  // naviagating back if the previous necessary fields are empty
  useEffect(() => {
    if(
      !(createOrder?.formData?.typeOfPaper) ||
      !(createOrder?.formData?.academicLevel) ||
      !(createOrder?.formData?.deadline) ||
      !(createOrder?.formData?.spacing) ||
      !(createOrder?.formData?.writingType) || 
      !(createOrder?.formData?.typeOfEnglish)  
    ){
      navigate("/calculation");
    }
  }, []);

  useEffect(() => {
    if (
      createOrder &&
      createOrder.formData &&
      Object.keys(createOrder.formData)?.length > 0
    ) {
      setFormData({
        ...formData,
        subject: createOrder.formData?.subject || "",
        topic: createOrder.formData?.topic || "Any Topic",
        orderInstructions: createOrder.formData?.orderInstructions || "",
        referenceStyle: createOrder.formData?.referenceStyle || "APA",
      });
      setUploadFiles(createOrder?.formDataFiles || []);
    }
  }, [createOrder]);

  const handleBack = () => {
    dispatch(setFormDataGlobState({ ...createOrder.formData, ...formData}));
    dispatch(setFormDataFiles(uploadFiles || []))
    navigate("/calculation");
  };

  const handleNext = async () => {
    // Mark all fields as touched
    setTouched({
      subject: true,
      topic: true,
      orderInstructions: true,
      referenceStyle: true,
      writingType: true,
      typeOfPaper: true,
      academicLevel: true,
      typeOfEnglish: true,
      deadline: true,
      spacing: true,
      pages: true,
      slides: true,
      needDocumentation: true,
      needPresentation: true,
      budgetIncrement: true
    });

    if (!validateForm()) {
      console.log("Validation failed");
      showToast("error", "Please fill all required fields");
      return;
    } else {
      submitOrder();
    }
  };

  // Function to submit the order
  const submitOrder = async () => {

    const fileUploadResult = await doUploadRefrencesToFirebase(
      uploadFiles,
      "order"
    );

    if (!fileUploadResult.success) {
      console.error("Upload Error:", fileUploadResult.error);
      showToast("error", fileUploadResult?.error?.message || "Failed to upload files");
      return;
    }

    const downloadURLs = fileUploadResult.uploadedUrls;

    // Update state with form data and uploaded file URLs
    dispatch(
      setFormDataGlobState({ ...formData })
    );

    const orderData = {
      ...formData,
      topic: formData.topic,
      referenceDocuments: downloadURLs,
    };

    // Call the placeOrder thunk
    const result = await doPlaceOrder(orderData);
    if (result.success) {
      // showToast("success", "Order placed successfully");
      setFormData({
        subject: "",
        topic: "",
        orderInstructions: "",
        referenceStyle: "APA"
      });
      dispatch(setFormDataGlobState({}));
      dispatch(setFormDataFiles([]));
      setUploadFiles([]);
      navigate(`/secure-payment/${result?.response?.data?._id || ''}`);
    } else {
      showToast("error", result?.error?.message);
    }
  };

  //Drag Upload
  const { getRootProps, getInputProps } = useDropzone({
    accept: referencesFormats,
    onDrop: (acceptedFiles) => {
      setUploadFiles((prev) => [...prev, ...acceptedFiles]);
    },
  });

  // console.log('createOrder peper instructuion', createOrder.formDataFiles)

  return (
    <div className="flex flex-col flex-grow h-screen min-w-[340px] overflow-x-hidden">
      {/* header section */}
      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] xl:mt-[24px] mt-[104px]">
        <h2 className=" block xl:hidden mr-2">Dashboard </h2>
      </div>

      {/* scrollable section */}
      <div className="overflow-y-auto xl:mt-10">
        <div className="bg-white  mt-[32px] ml-6 xl:ml-[48px] mr-[24px] lg:mr-[48px] mb-[48px] border border-medium-gray rounded-lg py-8 px-[16px] lg:px-[48px] xl:px-[136px]   md:py-10  max-w-full md:max-w-[2200px] w-auto mx-auto">
          <ProgressBar steps={steps} activeStep={2} completedStep={1} />{" "}
          <div className="flex flex-col justify-center items-center mt-[-80px] w-full">
            <div
              className="grid
      grid-cols-1
      gap-x-6 mt-10 w-full
      justify-center items-center
    "
            >
              <div className="mt-20">
                <CustomDropDown
                  label="Subject"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.subject && errors.subject}
                  defaultOption="Select a Subject"
                  idKey="id"
                  valueKey="name"
                  options={SUBJECTS_ARRAY}
                  dropdownDivStyle="w-full max-h-[240px] max-h-[166px]"
                />
              </div>

              <div className="mt-0">
                <LoginInput
                  className=" w-full 2xl:w-full px-4 "
                  id="topic"
                  name="topic"
                  label="Topic"
                  type="text"
                  value={formData.topic}
                  placeholder="Enter The Topic"
                  error={touched.topic && errors.topic}
                  onChange={(e) => handleInputChange("topic", e.target.value)}
                  onBlur={() => handleBlur("topic", formData.topic)}
                  isRequired={false}
                  isFloatingLabel={false}
                />
              </div>

              <div className="">
                <LoginInput
                  className=" w-full 2xl:w-full px-4 "
                  id="orderInstructions"
                  name="orderInstructions"
                  label="Order Instructions"
                  type="textarea"
                  value={formData.orderInstructions}
                  placeholder="Minimum 30 characters and maximum 1000 characters."
                  error={touched.orderInstructions && errors.orderInstructions}
                  onChange={(e) =>
                    handleInputChange("orderInstructions", e.target.value)
                  }
                  onBlur={() =>
                    handleBlur("orderInstructions", formData.orderInstructions)
                  }
                  isFloatingLabel={false}
                  inputStyle="w-full max-h-[166px] h-[166px] pb-[134px] text-left placeholder:whitespace-normal sm:placeholder:text-sm"
                />
              </div>

              <div className="">
                <CustomDropDown
                  label="Reference Style"
                  id="reference-style"
                  name="referenceStyle"
                  value={formData.referenceStyle}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.referenceStyle && errors.referenceStyle}
                  defaultOption="APA"
                  idKey="id"
                  valueKey="name"
                  options={REERENCE_STYLES}
                  dropdownDivStyle="w-full max-h-[240px] sm:w-[240px] md:w-[240px] lg:w-[320px] max-h-[166px]"
                />
              </div>

              <div className="w-full">
                <span className="lg:text-level-3 md:text-level-3 text-secondary font-medium leading-6 w-full xl:w-[664px] ">
                  Additional Documents/ Reference Materials
                </span>
                <div className="w-full">
                  <div className="flex flex-wrap  gap-2 lg:gap-4 m-2 lg:m-4">
                    {
                      uploadFiles.map((file, index) => {
                        return <CustomFileIcon 
                                  name={file.name} 
                                  extension={file.name.split('.').pop().toLowerCase()}
                                  downloadable={false}
                                  removeFile={() => setUploadFiles(prev => prev.filter((f, i) => i !== index))}
                                />
                      })
                    }
                  </div>
                </div>
                <div
                  {...getRootProps()}
                  className="border-dashed mt-3 border-2 border-blue-shades rounded p-4 h-[192px] flex flex-col items-center justify-center cursor-pointer"
                >
                  <input {...getInputProps()} className="hidden" />
                  <div className="">
                    {isUploadRefrencesToFirebaseLoading ? (
                      <ClipLoader
                        loading={
                          isUploadRefrencesToFirebaseLoading
                        }
                        speedMultiplier={1}
                        size={22}
                        color="#60a5fa"
                      />
                    ) : (
                      <div className=" relative">
                        <img
                          src={UploadIcon}
                          className="w-[51px] h-[43px]"
                          alt="Upload"
                        />
                        <span className=" w-[22px] h-[22px] rounded-full bg-blue-400 text-[14px] font-inter text-white flex items-center justify-center absolute -top-1 -right-1">
                          {uploadFiles?.length ||
                            createOrder.formData?.referenceDocuments?.length ||
                            0}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="mb-2 text-center text-secondary font-inter leading-5 mt-6 text-level-4">
                    Drag your file(s) to start uploading{" "} - Maximum size of a file is 20MB
                  </p>
                  <p className="text-gray text-level-5  mb-[8px]">OR</p>
                  <p className="border-[1px] border-blue-shades rounded-[8px] py-[6px] px-3 text-level-5 text-blue-shades font-semibold leading-[18px] ">
                    Browse files
                  </p>
                  {/* <p>
                    Maximum size of a file is 20MB
                  </p> */}
                </div>
              </div>

              <div className="flex justify-between mt-[16px] w-full">
                <CustomButton
                  text="Back"
                  iconDirection="left"
                  iconPosition="before"
                  onClick={handleBack}
                  width="120px"
                  height="44px"
                />
                <CustomButton
                  text={
                    isUploadRefrencesToFirebaseLoading || isPlacingOrder ? 
                    <ClipLoader
                      loading={true}
                      speedMultiplier={0.5}
                      size={22}
                      color="#60a5fa"
                    />
                    : "Next"
                  }               
                  type="submit"
                  iconDirection="right"
                  iconPosition="after"
                  onClick={handleNext}
                  width="120px"
                  height="44px"
                  disabled={
                    isUploadRefrencesToFirebaseLoading || isPlacingOrder
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaperInstructions;
